import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, Award, Users, Clock, Heart, Stethoscope, Activity, Shield, ArrowRight, CheckCircle, Star, Play, Quote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import HeroSection from '../components/HeroSection';
import SpecialtySection from '../components/SpecialtySection';
import DoctorCardGrid from '../components/DoctorCardGrid';

const Home = () => {
  const { t } = useTranslation();
  // Slideshow state
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // SOCH Images for slideshow
  const sochImages = [
    { src: '/assets/onedrive/SOCH (1).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 1' },
    { src: '/assets/onedrive/SOCH (2).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 2' },
    { src: '/assets/onedrive/SOCH (3).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 3' },
    { src: '/assets/onedrive/SOCH (4).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 4' },
    { src: '/assets/onedrive/SOCH (5).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 5' },
    { src: '/assets/onedrive/SOCH (6).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 6' },
    { src: '/assets/onedrive/SOCH (7).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 7' },
    { src: '/assets/onedrive/SOCH (8).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 8' },
    { src: '/assets/onedrive/SOCH (9).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 9' },
    { src: '/assets/onedrive/SOCH (10).jpeg', alt: 'Sharma Ortho Center Hospital - Facility 10' }
  ];

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sochImages.length);
    }, 4000); // Change slide every 4 seconds
    return () => clearInterval(interval);
  }, [sochImages.length]);

  // Testimonials data
  const testimonials = [
    {
      type: "written",
      name: "Rajesh Kumar",
      age: "45",
      treatment: "Knee Replacement Surgery",
      rating: 5,
      text: "Dr. Sharma and his team provided exceptional care during my knee replacement surgery. The entire process was smooth, and I'm now walking pain-free after years of discomfort. Highly recommend Sharma Ortho Center!",
      image: "/assets/testimonials/patient1.jpg",
      date: "2 months ago"
    },
    {
      type: "video",
      name: "Priya Mehta",
      age: "32",
      treatment: "Sports Injury Recovery",
      rating: 5,
      text: "Amazing experience with the physiotherapy team. They helped me recover from my sports injury completely.",
      image: "/assets/testimonials/patient2.jpg",
      videoUrl: "/assets/testimonials/video1.mp4",
      date: "1 month ago"
    },
    {
      type: "written",
      name: "Suresh Patel",
      age: "58",
      treatment: "Spine Surgery",
      rating: 5,
      text: "I was suffering from chronic back pain for over 5 years. Dr. Kumar's expertise in spine surgery gave me my life back. The post-operative care was outstanding, and the staff was incredibly supportive throughout my recovery journey.",
      image: "/assets/testimonials/patient3.jpg",
      date: "3 months ago"
    },
    {
      type: "video",
      name: "Anita Singh",
      age: "28",
      treatment: "Fracture Treatment",
      rating: 5,
      text: "Quick and effective treatment for my fracture. The doctors explained everything clearly and made sure I was comfortable.",
      image: "/assets/testimonials/patient4.jpg",
      videoUrl: "/assets/testimonials/video2.mp4",
      date: "2 weeks ago"
    },
    {
      type: "written",
      name: "Vikram Sharma",
      age: "41",
      treatment: "Joint Replacement",
      rating: 5,
      text: "The joint replacement surgery exceeded my expectations. The team's professionalism and the advanced facilities at Sharma Ortho Center made all the difference. I'm back to my active lifestyle within months!",
      image: "/assets/testimonials/patient5.jpg",
      date: "4 months ago"
    },
    {
      type: "written",
      name: "Meera Jain",
      age: "35",
      treatment: "Arthroscopy",
      rating: 5,
      text: "Minimally invasive arthroscopy procedure was performed perfectly. The recovery was much faster than I expected, and the results are excellent. Thank you to the entire team!",
      image: "/assets/testimonials/patient6.jpg",
      date: "6 weeks ago"
    }
  ];


  // Medical specialties based on the image
  const specialties = [
    {
      title: 'Complex Trauma & Fracture Surgery',
      description: 'Advanced treatment for complex bone fractures and trauma cases',
      icon: '🦴',
      gradient: 'from-red-500 to-pink-500'
    },
    {
      title: 'Joint Reconstruction',
      description: 'Hip, knee, and shoulder joint replacement surgeries',
      icon: '🔧',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Joint Replacement',
      description: 'Complete joint replacement with latest prosthetic technology',
      icon: '⚙️',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Arthroscopy & Sports Medicine',
      description: 'Minimally invasive procedures for sports injuries',
      icon: '🏃‍♂️',
      gradient: 'from-purple-500 to-violet-500'
    },
    {
      title: 'Ilizarov & Limb Surgery',
      description: 'Specialized limb lengthening and deformity correction',
      icon: '📏',
      gradient: 'from-orange-500 to-yellow-500'
    },
    {
      title: 'Deformity Correction',
      description: 'Correction of bone and joint deformities',
      icon: '🔄',
      gradient: 'from-teal-500 to-blue-500'
    },
    {
      title: 'Pediatric Orthopedic Club Foot (CTEV)',
      description: 'Specialized treatment for children\'s orthopedic conditions',
      icon: '👶',
      gradient: 'from-pink-500 to-rose-500'
    },
    {
      title: 'Neck & Back Pain',
      description: 'Comprehensive spine care and pain management',
      icon: '🏥',
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      title: 'Rheumatoid Arthritis & Ankylosing Spondylitis',
      description: 'Treatment for inflammatory joint conditions',
      icon: '💊',
      gradient: 'from-red-500 to-orange-500'
    },
    {
      title: 'Physiotherapy & Rehabilitation',
      description: 'Complete recovery and rehabilitation programs',
      icon: '🏋️‍♂️',
      gradient: 'from-green-500 to-teal-500'
    }
  ];

  const stats = [
    { icon: Users, number: '10,000+', label: 'Happy Patients' },
    { icon: Award, number: '15+', label: 'Years Experience' },
    { icon: Clock, number: '24/7', label: 'Emergency Care' },
    { icon: Heart, number: '98%', label: 'Success Rate' },
  ];

  const whyChooseUs = [
    {
      title: t('whyChooseUs.expertCare.title'),
      description: t('whyChooseUs.expertCare.description'),
      icon: '👨‍⚕️',
    },
    {
      title: t('whyChooseUs.advancedTechnology.title'),
      description: t('whyChooseUs.advancedTechnology.description'),
      icon: '🏥',
    },
    {
      title: t('whyChooseUs.comprehensiveCare.title'),
      description: t('whyChooseUs.comprehensiveCare.description'),
      icon: '💚',
    },
    {
      title: t('whyChooseUs.personalizedTreatment.title'),
      description: t('whyChooseUs.personalizedTreatment.description'),
      icon: '🎯',
    },
    {
      title: '24/7 Emergency Services',
      description: 'Round-the-clock emergency orthopedic care for trauma cases and urgent medical situations',
      icon: '🚨',
    },
    {
      title: 'Insurance Accepted',
      description: 'We accept all major insurance plans and offer flexible payment options for your convenience',
      icon: '💳',
    },
    {
      title: 'Modern Facilities',
      description: 'Clean, comfortable, and well-equipped hospital with private rooms and advanced operation theaters',
      icon: '🏢',
    },
    {
      title: 'Proven Success Rate',
      description: '98% success rate in surgeries with over 10,000 satisfied patients and positive outcomes',
      icon: '📈',
    },
  ];

  return (
    <div className="overflow-x-hidden">
      <HeroSection />

      {/* Brief Description Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                <Stethoscope className="w-4 h-4 mr-2" />
                {t('home.brief.badge')}
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                {t('home.brief.titleBefore')} 
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> {t('home.brief.titleHighlight')}</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                {t('home.brief.paragraph')}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{t('home.brief.features.expert.title')}</h4>
                    <p className="text-sm text-gray-600">{t('home.brief.features.expert.subtitle')}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{t('home.brief.features.advanced.title')}</h4>
                    <p className="text-sm text-gray-600">{t('home.brief.features.advanced.subtitle')}</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Auto-Swiper Slideshow */}
            <div className="relative mt-20">
              {/* Main slideshow container */}
              <div className="relative w-full max-w-7xl mx-auto h-96 rounded-3xl overflow-hidden shadow-2xl">
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600 rounded-3xl transform rotate-2 opacity-20 -z-10 scale-185"></div>
                
                {/* Images container */}
                <div className="relative w-full h-full">
                  {sochImages.map((image, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                        index === currentSlide 
                          ? 'opacity-100 transform translate-x-0' 
                          : index === (currentSlide - 1 + sochImages.length) % sochImages.length
                          ? 'opacity-0 transform -translate-x-full'
                          : 'opacity-0 transform translate-x-full'
                      }`}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover rounded-3xl"
                        loading="lazy"
                        onError={(e) => {
                          console.error(`Failed to load image: ${image.src}`);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  ))}
                </div>


                {/* Slide indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                  {sochImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentSlide 
                          ? 'bg-white scale-125' 
                          : 'bg-white/60 hover:bg-white/80'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>

              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-20 bg-white relative">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 to-white"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-6">
              <Activity className="w-4 h-4 mr-2" />
              {t('home.specialties.badge')}
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('home.specialties.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.specialties.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
            {specialties.map((specialty, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 hover:border-blue-200"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${specialty.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}></div>
                <div className="relative z-10">
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {specialty.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                    {specialty.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {specialty.description}
                  </p>
                  <div className="mt-4 flex items-center text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-sm font-semibold">Learn More</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Foot & Ankle Surgery Highlight */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-32 -translate-x-32"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: concise ankle message */}
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold">
                <Heart className="w-4 h-4 mr-2 fill-current" />
                {t('home.ankle.badge')}
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                {t('home.ankle.title')}
              </h2>
              <p className="text-xl text-blue-100 leading-relaxed">
                {t('home.ankle.description')}
              </p>
              <div className="grid sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-blue-100">{t('home.ankle.items.sprains')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-blue-100">{t('home.ankle.items.fractures')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-blue-100">{t('home.ankle.items.achilles')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-blue-100">{t('home.ankle.items.ligament')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-blue-100">{t('home.ankle.items.arthritis')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-blue-100">{t('home.ankle.items.chronicPain')}</span>
                </div>
              </div>
              <div className="pt-4">
                <p className="text-lg text-white font-semibold mb-3">{t('home.ankle.question')}</p>
                <a
                  href="tel:+917727048951"
                  className="inline-flex items-center px-5 py-3 bg-white text-blue-700 font-semibold rounded-xl shadow hover:shadow-lg hover:bg-blue-50 transition-all"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {t('home.ankle.callNow')} +91 77270 48951
                </a>
              </div>
            </div>

            {/* Right: display ankle image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 h-[420px]">
              <img
                src="/assets/onedrive/ankle.png"
                alt="Ankle treatment"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Maa Yojna & Appointment Section - Two Column Layout */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative overflow-hidden">
        {/* Soft Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-200/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-200/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            
            {/* Left Column - Maa Yojna Initiative */}
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 lg:p-10 shadow-xl border border-white/60 hover:shadow-2xl transition-all duration-500 group">
              <div className="space-y-6">
                {/* Logo */}
                <div className="flex justify-center lg:justify-start">
                  <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 rounded-2xl shadow-lg group-hover:scale-105 transition-transform duration-300">
                    <img
                      src="/assets/onedrive/MAA-Y-removebg-preview.png"
                      alt="Maa Yojna Logo"
                      className="h-24 w-auto object-contain"
                    />
                  </div>
                </div>

                {/* Title */}
                <div>
                  <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {t('home.maaYojna.title')}
                    </span>
                  </h3>
                  <div className="h-1 w-24 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {t('home.maaYojna.p1')}
                  </p>
                  <p className="text-base text-gray-600 leading-relaxed">
                    {t('home.maaYojna.p2')}
                  </p>
                </div>

                {/* Benefits List */}
                <div className="space-y-3 pt-4">
                  <div className="flex items-start space-x-3">
                    <div className="mt-1 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-gray-700 font-medium">{t('home.maaYojna.benefits.b1')}</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="mt-1 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-gray-700 font-medium">{t('home.maaYojna.benefits.b2')}</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="mt-1 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-gray-700 font-medium">{t('home.maaYojna.benefits.b3')}</p>
                  </div>
                </div>

                {/* CTA */}
                <div className="pt-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl group-hover:scale-105 transform"
                  >
                    {t('home.maaYojna.learnMore')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Right Column - Book Appointment */}
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 lg:p-10 shadow-xl border border-white/60 hover:shadow-2xl transition-all duration-500">
              <div className="space-y-6">
                {/* Header */}
                <div className="text-center lg:text-left">
                    <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
                    <Calendar className="w-4 h-4 mr-2" />
                    {t('home.booking.scheduleBadge')}
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
                    {t('home.booking.title1')}
                    <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {t('home.booking.title2')}
                    </span>
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {t('home.booking.description')}
                  </p>
                </div>

                {/* Appointment Options */}
                <div className="space-y-4">
                  {/* Online Booking */}
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 border-2 border-yellow-200 hover:border-yellow-400 transition-all duration-300 group cursor-pointer hover:shadow-lg">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-yellow-400 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg">
                        <Calendar className="w-7 h-7 text-yellow-900" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">{t('home.booking.online.title')}</h4>
                        <p className="text-gray-600 text-sm mb-3">{t('home.booking.online.subtitle')}</p>
                        <Link
                          to="/contact"
                          className="inline-flex items-center text-yellow-700 font-semibold hover:text-yellow-800 transition-colors"
                        >
                          {t('home.booking.online.cta')}
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Call to Book */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200 hover:border-green-400 transition-all duration-300 group cursor-pointer hover:shadow-lg">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-green-400 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg">
                        <Phone className="w-7 h-7 text-green-900" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">{t('home.booking.call.title')}</h4>
                        <p className="text-gray-600 text-sm mb-3">{t('home.booking.call.subtitle')}</p>
                        <a
                          href="tel:+917727048951"
                          className="inline-flex items-center text-green-700 font-semibold hover:text-green-800 transition-colors"
                        >
                          {t('home.booking.call.cta')}
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Emergency Care */}
                  <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 border-2 border-red-200 hover:border-red-400 transition-all duration-300 group cursor-pointer hover:shadow-lg">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-red-400 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg">
                        <Heart className="w-7 h-7 text-red-900" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">{t('home.booking.emergency.title')}</h4>
                        <p className="text-gray-600 text-sm mb-3">{t('home.booking.emergency.subtitle')}</p>
                        <a
                          href="tel:+917727048951"
                          className="inline-flex items-center text-red-700 font-semibold hover:text-red-800 transition-colors"
                        >
                          {t('home.booking.emergency.cta')}
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust Indicators */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{t('home.booking.trust.sameDay')}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{t('home.booking.trust.insurance')}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Award className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{t('home.booking.trust.yearsCare')}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{t('home.booking.trust.patients')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <DoctorCardGrid />

      {/* Testimonials Section */}
      <section className="py-20 bg-white relative">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-white to-purple-50/30"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
            <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 bg-yellow-100 text-yellow-800 rounded-full font-semibold mb-6">
              <Quote className="w-5 h-5 mr-2" />
              {t('home.testimonialsSection.badge')}
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('home.testimonialsSection.title1')}
              <span className="block bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
                {t('home.testimonialsSection.title2')}
              </span>
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('home.testimonialsSection.description')}
            </p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-yellow-200 p-8">
                {/* Testimonial Type Badge */}
                <div className="flex items-center justify-between mb-6">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                    testimonial.type === 'video' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {testimonial.type === 'video' ? (
                      <>
                        <Play className="w-4 h-4 mr-1" />
                        {t('home.testimonialsSection.video')}
                      </>
                    ) : (
                      <>
                        <Quote className="w-4 h-4 mr-1" />
                        {t('home.testimonialsSection.written')}
                      </>
                    )}
                  </div>
                  
                  {/* Rating Stars */}
                  <div className="flex items-center">
                    {[...Array(testimonial.rating)].map((_, starIndex) => (
                      <Star key={starIndex} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>

                {/* Video Thumbnail or Quote */}
                {testimonial.type === 'video' ? (
                  <div className="relative mb-6 group-hover:scale-105 transition-transform duration-300">
                    <div className="relative h-48 bg-gradient-to-br from-red-100 to-pink-100 rounded-xl overflow-hidden">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      {/* Fallback */}
                      <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-pink-500 hidden items-center justify-center">
                        <Play className="w-12 h-12 text-white" />
                      </div>
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                        <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="w-8 h-8 text-red-600 ml-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-6">
                    <Quote className="w-8 h-8 text-yellow-400 mb-4" />
                  </div>
                )}

                {/* Testimonial Text */}
                <blockquote className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </blockquote>

                {/* Patient Info */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white font-bold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">Age {testimonial.age} • {testimonial.treatment}</p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm text-gray-500">{testimonial.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">{t('home.testimonialsSection.ctaTitle')}</h3>
              <p className="text-lg mb-6 opacity-90">{t('home.testimonialsSection.ctaSubtitle')}</p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-yellow-600 font-bold rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <Calendar className="mr-2 w-5 h-5" />
                  {t('home.testimonialsSection.ctaBook')}
                </Link>
                
                <a
                  href="tel:+917727048951"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/40 text-white font-bold rounded-xl hover:bg-white/20 transition-all duration-300"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  {t('home.testimonialsSection.ctaCall')} +91 77270 48951
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center animate-fade-in">
                <div className="bg-primary-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="h-8 w-8 text-primary-600" />
                </div>
                <div className="text-3xl font-bold text-primary-600 mb-2">{stat.number}</div>
                <div className="text-gray-600">
                  {index === 0 && t('home.statsAlt.happyPatients')}
                  {index === 1 && t('home.statsAlt.yearsExperience')}
                  {index === 2 && t('home.statsAlt.emergencyCare')}
                  {index === 3 && t('home.statsAlt.successRate')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose SOCH Section - Modern Premium Design */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50/40 relative overflow-hidden">
        {/* Modern Geometric Background */}
        <div className="absolute inset-0 opacity-30">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full blur-xl"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-blue-200 to-blue-300 rounded-2xl rotate-45 blur-lg"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-blue-150 to-blue-250 rounded-full blur-lg"></div>
          <div className="absolute bottom-20 right-1/3 w-28 h-28 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl rotate-12 blur-xl"></div>
          
          {/* Subtle Grid Pattern */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        {/* Subtle Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Enhanced Header Section */}
          <div className="text-center mb-16">
            {/* Decorative Element */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 shadow-lg shadow-blue-500/25">
              <Shield className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              {t('whyChooseUs.title')} 
              <span className="block mt-2 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 bg-clip-text text-transparent">
                {t('whyChooseUs.titleHighlight')}
              </span>
            </h2>
            
            <div className="max-w-3xl mx-auto">
              <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed mb-4">
                {t('whyChooseUs.subtitle')} 
                <span className="font-semibold text-gray-800"> {t('whyChooseUs.subtitleHighlight')}</span> {t('whyChooseUs.subtitleEnd')}
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full mx-auto"></div>
            </div>
          </div>

          {/* Clean Professional Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index} 
                className="group relative bg-white rounded-2xl p-8 text-center transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10 hover:border-blue-200 border border-gray-100 shadow-md shadow-gray-200/40"
                style={{
                  animationDelay: `${index * 150}ms`
                }}
              >
                {/* Subtle Top Accent */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-b-full group-hover:w-20 transition-all duration-300"></div>
                
                {/* Subtle Background Tint on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-blue-100/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Content */}
                <div className="relative z-10 pt-4">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 group-hover:text-blue-700 transition-colors duration-300 leading-tight">
                    {item.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base group-hover:text-gray-800 transition-colors duration-300 min-h-[4rem]">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Call to Action */}
          <div className="text-center mt-16">
            <div className="inline-block relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl blur-xl opacity-25 scale-110 group-hover:opacity-40 transition-opacity duration-500"></div>
              
              <Link
                to="/contact"
                className="relative inline-flex items-center px-10 py-4 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white font-semibold text-lg rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-500 hover:scale-105 transform group overflow-hidden"
              >
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <span className="relative z-10">{t('whyChooseUs.bookConsultation')}</span>
                <ArrowRight className="relative z-10 ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            
            <p className="text-gray-500 text-sm mt-4 font-light">
              {t('cta.subtitle')}
            </p>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-blue-600/5 rounded-full blur-3xl"></div>
      </section>

      {/* Emergency Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            🚨 {t('home.emergency.title')}
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            {t('home.emergency.desc')}
          </p>
          <a
            href="tel:+917727048951"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition duration-300 transform hover:scale-105"
          >
            <Phone className="mr-2 h-5 w-5" />
            {t('home.emergency.call')} +91 77270 48951
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
